import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import BaseLayout from './BaseLayout';
import { apiErrorMessage } from './prescriptionForm';
import {
  EQUIPMENT_PROFILES,
  WEEKDAYS,
  MIN_DAYS,
  MAX_DAYS,
  emptySurvey,
  toggleDay,
  surveyProblem,
  canSubmit,
  surveyRequest,
  isGenerating,
  isReady,
  historyNote,
  proseList,
  hasFailed,
  daysLabel,
  chosenDays,
  startsLabel,
  substitutionLabels,
  weekHref,
} from './selfProgram';
import '../styles/SelfProgramPage.css';

// "Write me a program".
//
// # THE LOOP
//
// Ask, generate, activate. Generation is a full LLM round trip so the server
// answers 202 and this polls — there is no version of this that waits on a
// request, for the reasons drafts.go sets out.
//
// # IT HAS TO SURVIVE THE PAGE NOT BEING THERE
//
// Minutes of waiting, on a phone, in a gym. The screen will lock, the tab will be
// backgrounded, the app will be reopened. The draft is a row on the server, so on
// mount this asks whether one of the athlete's own is still running and picks the
// polling back up rather than stranding it.

const POLL_INTERVAL_MS = 2000;
// ~20s of failed polls before the athlete is told anything. Below this a single
// dropped request would raise an alarm about a generation that is fine.
const MAX_POLL_FAILURES = 10;

/** One equipment choice. */
const EquipmentChoice = ({ profile, selected, onChange }) => (
  <label
    className={`self-choice ${selected ? 'is-selected' : ''}`}
    key={profile.value}
  >
    <input
      type="radio"
      name="equipment"
      value={profile.value}
      checked={selected}
      onChange={() => onChange(profile.value)}
    />
    <span className="self-choice-label">{profile.label}</span>
    <span className="self-choice-detail muted">{profile.detail}</span>
  </label>
);

/** The weekday picker. */
const DayPicker = ({ days, onToggle }) => (
  <div className="self-days" role="group" aria-label="Training days">
    {WEEKDAYS.map((weekday) => {
      const selected = days.includes(weekday.value);
      return (
        <button
          key={weekday.value}
          type="button"
          className={`self-day ${selected ? 'is-selected' : ''}`}
          aria-pressed={selected}
          onClick={() => onToggle(weekday.value)}
        >
          {weekday.short}
        </button>
      );
    })}
  </div>
);

/*
 * What the athlete's own log contributed, or nothing at all.
 *
 * Rendered directly under the heading because it answers the question somebody
 * is actually asking at this moment — where did this come from. Below the
 * "we will put it in your week" line it reads as a footnote to a shipping
 * notice. See historyNote for why silence is the common case.
 */
const HistoryNote = ({ state }) => {
  const note = historyNote(state);
  if (!note) return null;

  return (
    <p className="meta-copy self-history">
      Written from <strong>{note.source}</strong>. Your training set{' '}
      {proseList(note.contributions)}.
    </p>
  );
};

const SelfProgramPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState(emptySurvey);
  const [state, setState] = useState(null);
  const [activation, setActivation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bumped after EVERY poll attempt, so the effect below reschedules itself
  // whatever the outcome. Rescheduling used to be a side effect of setState
  // receiving a new object, which meant a failed request simply stopped it.
  const [tick, setTick] = useState(0);

  // Sharing consent, asked here because this is the one screen every new
  // athlete passes through. See the fieldset below for what that costs.
  //
  // Two values, not one. `consentLoaded` is what the server said when this
  // screen opened; `consent` is what the checkbox says now. The difference is
  // the whole guard: an athlete who turned sharing ON from /my-training and then
  // came here would have it silently turned back OFF by a box that defaulted to
  // unchecked — a revocation they never asked for, performed by a form about
  // something else. Nothing is written unless the two differ.
  const [consent, setConsent] = useState(false);
  const [consentLoaded, setConsentLoaded] = useState(false);
  const [consentFailed, setConsentFailed] = useState(false);

  const timer = useRef(null);
  const failures = useRef(0);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // Pick up an in-flight draft from a previous visit. A 204 means there is
  // nothing running, which is the ordinary case and not an error; a 404 means
  // this server has no generation configured at all, and the athlete should
  // never have reached this screen.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await axios.get(buildApiUrl('/self-programs'), authConfig());
        if (!cancelled && response.status === 200 && response.data?.draftId) {
          setState(response.data);
        }
      } catch (err) {
        if (!cancelled && err.response?.status === 404) {
          setError('Writing programmes is not available on this server.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Read the athlete's current answer so the box reflects it. A failure
    // leaves both values false, so the box is unchecked and nothing is written
    // — the quiet outcome is "not shared", never "shared".
    (async () => {
      try {
        const response = await axios.get(buildApiUrl('/consent'), authConfig());
        if (cancelled) return;
        const agreed = Boolean(response.data?.learningConsent);
        setConsent(agreed);
        setConsentLoaded(agreed);
      } catch {
        // Deliberately silent. A failure here must not tell somebody their
        // programme cannot be written.
      }
    })();

    return () => { cancelled = true; };
  }, [authConfig]);

  // Poll while it is being written.
  //
  // # WHY THE TICK
  //
  // This effect's deps used to be [state, authConfig] alone, so the next timeout
  // was only ever scheduled because setState handed back a new object. Both
  // non-200 paths therefore ENDED the polling silently:
  //
  //   - a network error set an error and never touched state, so one dropped
  //     request in a 78-330s wait on gym wifi stranded the athlete for good;
  //   - a 204 — which is exactly what selfDraftAbandoned returns once a pending
  //     draft outlives the timeout — was ignored, so the page sat on "Writing
  //     your programme" forever, defeating the server-side recovery written to
  //     prevent that.
  //
  // `tick` is bumped after every attempt, so rescheduling no longer depends on
  // the response being the good one.
  //
  // # WHY A FAILURE BUDGET
  //
  // A single blip on a phone is normal and self-heals on the next tick; showing
  // an error for it teaches the athlete to distrust a screen that is working.
  // The message appears only once the failures look persistent, and clears the
  // moment a poll succeeds.
  useEffect(() => {
    if (!isGenerating(state)) return undefined;

    timer.current = setTimeout(async () => {
      try {
        const response = await axios.get(buildApiUrl('/self-programs'), authConfig());
        if (response.status === 200) {
          failures.current = 0;
          setError(null);
          setState(response.data);
        } else if (response.status === 204) {
          // The draft is gone — the server gave up on it. Back to the survey
          // rather than a spinner that never resolves.
          failures.current = 0;
          setState(null);
          setError('That programme stopped before it finished. You can start again.');
          return;                       // deliberately not rescheduled
        }
      } catch (err) {
        failures.current += 1;
        if (failures.current >= MAX_POLL_FAILURES) {
          setError('Still trying to reach your programme — check your connection.');
        }
      }
      setTick((t) => t + 1);            // always keep polling
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(timer.current);
  }, [state, tick, authConfig]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const start = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setConsentFailed(false);

    // Consent is recorded BEFORE the programme is written, and separately.
    //
    // # WHY IT CANNOT BLOCK GENERATION
    //
    // The athlete came here for a programme. A consent write that failed and
    // took the programme down with it would make answering the question
    // costlier than ignoring it, which is the opposite of how a consent
    // question should behave. A failure is reported; the generation proceeds.
    //
    // # WHY THE FAILURE IS REPORTED AT ALL
    //
    // Failing to record a YES leaves sharing off — the safe direction — but an
    // athlete who ticked the box believes they are sharing. Saying nothing
    // would leave them holding a false belief about their own data.
    if (consent !== consentLoaded) {
      try {
        await axios.put(
          buildApiUrl('/consent'),
          { learningConsent: consent },
          authConfig()
        );
        setConsentLoaded(consent);
      } catch {
        setConsentFailed(true);
      }
    }

    try {
      const response = await axios.post(
        buildApiUrl('/self-programs'),
        surveyRequest(survey),
        authConfig()
      );
      setState(response.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not start writing your programme.'));
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await axios.post(
        buildApiUrl(`/programs/${state.programId}/drafts/${state.draftId}/activate`),
        null,
        authConfig()
      );
      setActivation(response.data?.activated ?? null);
      setState({ ...state, status: 'committed' });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not build your programme.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <BaseLayout><p className="self-status">Loading…</p></BaseLayout>;
  }

  const problem = surveyProblem(survey);

  return (
    <BaseLayout>
      <div className="self-program">
        <header className="self-header">
          <span className="pill">Your training</span>
          <h1 className="page-title">Write me a program</h1>
        </header>

        {error && <p className="status-error" role="alert">{error}</p>}

        {/* Outside the form on purpose. Submitting replaces the form with the
            progress view, so a notice rendered inside it is unmounted in the
            same tick it is set — the athlete ticks a box, the write fails, and
            nothing ever tells them. It belongs where the page's own errors live,
            which is the one region every state renders. */}
        {consentFailed && (
          <p className="meta-copy self-problem" role="status">
            Your programme is being written, but we could not save your sharing
            choice — it is unchanged for now. You can set it from{' '}
            <Link className="self-inline-link" to="/my-training">your training data</Link>.
          </p>
        )}

        {/* Done. The first thing said is when they train, because the week is
            legitimately empty until that day arrives and an unexplained empty
            week reads as a failure. */}
        {activation && (
          <section className="card self-done">
            <h2 className="section-title">{activation.programName} is ready</h2>
            {/* The server's answer, not the form's. A reloaded page has no
                memory of the survey and its state is the default Mon/Wed/Fri —
                which would be rendered as a confident, specific, wrong sentence
                to anybody who picked anything else. */}
            <p className="meta-copy">
              {activation.sessions} session{activation.sessions === 1 ? '' : 's'} a week,
              on {daysLabel(chosenDays(activation, survey))}.
            </p>
            {activation.unscheduled > 0 && (
              <p className="meta-copy muted">
                One extra session was written but has no day yet — you can place it
                from your programme.
              </p>
            )}
            {startsLabel(activation) && (
              <p className="meta-copy self-starts">{startsLabel(activation)}</p>
            )}

            {substitutionLabels(activation.substitutions).length > 0 && (
              <div className="self-changes">
                <h3 className="self-changes-title">What we changed for your setup</h3>
                <ul>
                  {substitutionLabels(activation.substitutions).map((label) => (
                    <li key={label} className="muted">{label}</li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="button"
              className="btn-primary self-go"
              // The week the block was written in — the one surface that IS a
              // week. This button says "my week", and until now it landed on
              // /training, the athlete's dashboard, which is not a week: the
              // label promised something the destination did not show. The
              // activation carries the ids for the week view (weekHref); we fall
              // back to /training only if they are somehow missing.
              onClick={() => navigate(weekHref(activation) ?? '/training')}
            >
              See my week
            </button>
          </section>
        )}

        {/* Being written. */}
        {!activation && isGenerating(state) && (
          <section className="card self-waiting" aria-live="polite">
            <h2 className="section-title">Writing your programme</h2>
            <p className="meta-copy">
              This takes a few minutes. You can close this and come back — we will
              pick up where it got to.
            </p>
          </section>
        )}

        {/* Ready to build. A separate press, so nothing is assigned to somebody
            who wandered off mid-generation. */}
        {!activation && isReady(state) && (
          <section className="card self-ready">
            <h2 className="section-title">Your programme is written</h2>
            <HistoryNote state={state} />
            <p className="meta-copy">
              We will fit it to your equipment and put it in your week.
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={activate}
              disabled={busy}
            >
              {busy ? 'Building…' : 'Put it in my week'}
            </button>
          </section>
        )}

        {!activation && hasFailed(state) && (
          <section className="card self-failed">
            <h2 className="section-title">That did not work</h2>
            <p className="meta-copy">{state.error || 'Something went wrong writing your programme.'}</p>
            <button type="button" className="btn-secondary" onClick={() => setState(null)}>
              Try again
            </button>
          </section>
        )}

        {/* The survey. Hidden once something is in flight, so there is one thing
            to do on the screen at a time. */}
        {!activation && !state && (
          <form className="card self-survey" onSubmit={start}>
            <fieldset className="self-field">
              <legend className="section-title">What do you train with?</legend>
              <div className="self-choices">
                {EQUIPMENT_PROFILES.map((profile) => (
                  <EquipmentChoice
                    key={profile.value}
                    profile={profile}
                    selected={survey.equipment === profile.value}
                    onChange={(equipment) => setSurvey({ ...survey, equipment })}
                  />
                ))}
              </div>
            </fieldset>

            <fieldset className="self-field">
              <legend className="section-title">Which days do you train?</legend>
              <p className="meta-copy">
                Between {MIN_DAYS} and {MAX_DAYS} days. We put one session on each.
              </p>
              <DayPicker
                days={survey.days}
                onToggle={(day) => setSurvey({ ...survey, days: toggleDay(survey.days, day) })}
              />
              {survey.days.length > 0 && (
                <p className="meta-copy muted">{daysLabel(survey.days)}</p>
              )}
            </fieldset>

            <fieldset className="self-field">
              <legend className="section-title">Anything else? (optional)</legend>
              <label className="self-input">
                <span>What are you training for?</span>
                <input
                  type="text"
                  value={survey.goals}
                  placeholder="Get stronger, put on size…"
                  onChange={(e) => setSurvey({ ...survey, goals: e.target.value })}
                />
              </label>
              <label className="self-input">
                <span>Anything we should work around?</span>
                <input
                  type="text"
                  value={survey.injuries}
                  placeholder="Sore left shoulder…"
                  onChange={(e) => setSurvey({ ...survey, injuries: e.target.value })}
                />
              </label>
            </fieldset>

            {/*
              Sharing consent, asked on the one screen every new athlete passes
              through.

              # WHAT IS WRONG WITH ASKING HERE, SAID PLAINLY

              This is a form somebody fills in because they want something. A
              consent question on that path is asked at the moment consent is
              least freely given, and no amount of wording fixes that — it is
              structural. It was asked here anyway because the alternative was
              continuing not to ask at all: sharing defaults to off, the only
              control lived on /my-training, and the funnel never went there. An
              athlete's first eight logged sets were withheld by a question
              nobody had put to them.

              # WHAT IS DONE ABOUT IT

              Unchecked unless the server says otherwise. Not bundled with the
              submit — the programme is written identically either way, and
              canSubmit does not read it. No pre-ticking, no "recommended", no
              consequence for declining. It says it is reversible because it is,
              and it names where to reverse it.
            */}
            <fieldset className="self-field self-consent">
              <legend className="section-title">Your training data</legend>
              <label className="self-check">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>Use my training to improve my programmes</span>
              </label>
              <p className="meta-copy muted">
                Your logged sets help the generator learn what works for you.
                Leaving this unticked changes nothing about the programme you
                are about to get. You can turn it on or off at any time from{' '}
                <Link className="self-inline-link" to="/my-training">your training data</Link>.
              </p>
            </fieldset>

            {problem && <p className="meta-copy self-problem">{problem}</p>}

            <div className="self-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={busy || !canSubmit(survey)}
              >
                {busy ? 'Starting…' : 'Write me a program'}
              </button>
              <Link className="btn-secondary" to="/training">Cancel</Link>
            </div>
          </form>
        )}
      </div>
    </BaseLayout>
  );
};

export default SelfProgramPage;
